import { FC, MutableRefObject, ReactNode, useMemo, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Password } from 'primereact/password';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { CircleCheck, Info, SquarePen, UserPlus } from 'lucide-react';
import { allGrades, allSites, isStudentInManyClasses } from '../../../graphQLOperations/Classes';
import { ClassesAllGradesQuery } from '../../../graphQLOperations/__generated__/ClassesAllGradesQuery.graphql';
import { ClassesAllSitesQuery } from '../../../graphQLOperations/__generated__/ClassesAllSitesQuery.graphql';
import { ClassesStudentWithManyClassesQuery } from '../../../graphQLOperations/__generated__/ClassesStudentWithManyClassesQuery.graphql';
import { createUsers, updateUser } from '../../../graphQLOperations/Users';
import { UsersCreateUsersMutation } from '../../../graphQLOperations/__generated__/UsersCreateUsersMutation.graphql';
import { UsersUpdateUsersMutation } from '../../../graphQLOperations/__generated__/UsersUpdateUsersMutation.graphql';
import { upsertStudentTargetGroupsForStudent } from '../../../graphQLOperations/Diagnostic';
import { DiagnosticUpsertStudentTargetGroupsForStudentMutation } from '../../../graphQLOperations/__generated__/DiagnosticUpsertStudentTargetGroupsForStudentMutation.graphql';
import { isValidEmail } from '../../../businessLogicService/emailValidator';
import { splitId } from '../../../businessLogicService/splitIdsFromBackEnd';
import { ClassUser } from '../../../models/Class';
import { Grade } from '../../../models/Grade';
import { TargetGroup } from '../../../models/TargetGroup';
import { MaskedInput } from '../../MaskedData/MaskedInput';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../../analytics/dataMasking';
import { ErrorMessages, WizardUser } from './wizardTypes';

interface EmptyValues {
  firstName: string;
  lastName: string;
  emailAddress: string;
  grade: Grade | null;
  roadmap: TargetGroup | null;
}

interface Props {
  visible: boolean;
  onHide: () => void;
  siteId: string;
  siteName: string;
  toast: MutableRefObject<Toast | null>;
  onCreated?: (student: WizardUser) => void;
  /** When provided, the dialog edits this existing student instead of creating a new one. */
  student?: ClassUser;
  /** Clever-imported students/classes only allow editing the Roadmap; every other field is disabled. */
  isExternal?: boolean;
  onSaved?: () => void;
  /** When provided, the Site field becomes a selectable dropdown (Users page) instead of a fixed value (class roster). Only used while creating; site is locked once editing. */
  siteOptions?: { id: string; name: string }[];
  onSiteChange?: (site: { id: string; name: string }) => void;
}

const emptyValues: EmptyValues = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  grade: null as Grade | null,
  roadmap: null as TargetGroup | null,
};

const ChecklistItem: FC<{ met: boolean; children: ReactNode }> = ({ met, children }) => (
  <li className="flex align-items-center gap-xs">
    <CircleCheck size={16} className={met ? 'tw-text-emerald-600' : 'tw-text-neutral-300'} aria-hidden />
    <span className={met ? '' : 'text-color-secondary'}>{children}</span>
  </li>
);

const ChecklistSubItem: FC<{ met: boolean; children: ReactNode }> = ({ met, children }) => (
  <li className="flex align-items-center gap-xs">
    <span
      className={`tw-inline-block tw-h-2 tw-w-2 tw-rounded-full ${met ? 'tw-bg-emerald-600' : 'tw-bg-neutral-300'}`}
      aria-hidden
    />
    <span className={met ? '' : 'text-color-secondary'}>{children}</span>
  </li>
);

const ClassWizardNewStudentDialog: FC<Props> = ({
  visible,
  onHide,
  siteId,
  siteName,
  onCreated,
  student,
  isExternal,
  onSaved,
  toast,
  siteOptions,
  onSiteChange,
}) => {
  const isEdit = Boolean(student);
  const currentRoadmap = student?.studentTargetGroups?.find(tg => tg.isCurrent)?.targetGroup;

  const [values, setValues] = useState(() =>
    student
      ? {
          firstName: student.firstName,
          lastName: student.lastName,
          emailAddress: student.email ?? '',
          grade: student.gradeLevels?.[0]
            ? ({ id: student.gradeLevels[0].id, name: student.gradeLevels[0].name, order: 0 } as Grade)
            : null,
          roadmap: currentRoadmap ?? null,
        }
      : emptyValues,
  );
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const gradesData = useLazyLoadQuery<ClassesAllGradesQuery>(allGrades, {});
  const [createStudentMutation, creating] = useMutation<UsersCreateUsersMutation>(createUsers);
  const [updateStudentMutation, updating] = useMutation<UsersUpdateUsersMutation>(updateUser);
  const [upsertRoadmap, savingRoadmap] = useMutation<DiagnosticUpsertStudentTargetGroupsForStudentMutation>(
    upsertStudentTargetGroupsForStudent,
  );

  const { isUserInMoreThanOneClass } = useLazyLoadQuery<ClassesStudentWithManyClassesQuery>(isStudentInManyClasses, {
    userId: student?.id ?? '',
    skip: !isEdit,
  });

  const grades = useMemo(
    () => [...gradesData.allGradeLevels].sort((a, b) => a.order - b.order) as Grade[],
    [gradesData],
  );
  const sitesData = useLazyLoadQuery<ClassesAllSitesQuery>(allSites, {
    input: { siteIds: siteId ? [siteId] : [] },
  });
  const roadmapOptions = useMemo(
    () => (sitesData.sites?.nodes?.find(site => site?.id === siteId)?.targetGroups ?? []) as TargetGroup[],
    [sitesData, siteId],
  );

  const passwordChecks = {
    minLength: password.length >= 10,
    hasLower: /[a-z]/.test(password),
    hasUpper: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    noTripleRepeat: !/(.)\1\1/.test(password),
  };
  const passwordClassesMet = [
    passwordChecks.hasLower,
    passwordChecks.hasUpper,
    passwordChecks.hasNumber,
    passwordChecks.hasSpecial,
  ].filter(Boolean).length;
  const passwordMeetsThreeClasses = passwordClassesMet >= 3;
  const isPasswordValid =
    !password || (passwordChecks.minLength && passwordMeetsThreeClasses && passwordChecks.noTripleRepeat);

  const errors: ErrorMessages = {
    firstName: values.firstName.trim() ? '' : 'First name is required.',
    lastName: values.lastName.trim() ? '' : 'Last name is required.',
    emailAddress: !values.emailAddress.trim()
      ? 'Email address is required.'
      : !isValidEmail(values.emailAddress)
      ? 'Invalid email address.'
      : '',
    grade: values.grade ? '' : 'Grade is required.',
    site: siteId ? '' : 'Site is required.',
    roadmap: values.roadmap ? '' : 'Roadmap is required.',
    password: isPasswordValid ? '' : 'Password does not meet the requirements.',
  };
  const isValid = Object.values(errors).every(message => !message);
  const roadmapChanged = currentRoadmap?.id !== values.roadmap?.id;

  const close = () => {
    setValues(emptyValues);
    setSubmitted(false);
    onHide();
  };

  const showError = (message?: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail:
        message ?? 'There was an error. Usually this happens when an account with that email address already exists.',
    });
  };

  const saveRoadmapIfChanged = (studentId: string, onDone: () => void) => {
    if (isEdit && !roadmapChanged) {
      onDone();
      return;
    }
    upsertRoadmap({
      variables: {
        input: {
          studentTargetGroups: [
            { studentId, targetGroupId: values.roadmap?.id ?? '', isCurrent: true, isActive: true },
          ],
        },
      },
      onCompleted: onDone,
      onError: () => showError(),
    });
  };

  const finishEdit = () => {
    onSaved?.();
    toast.current?.show({
      severity: 'success',
      detail: (
        <>
          <MaskedStudentData student={{ publicUserId: student?.publicUserId }} type={PiiTypes.FullName}>
            {`${values.firstName} ${values.lastName}`}
          </MaskedStudentData>
          {' has been updated.'}
        </>
      ),
    });
    close();
  };

  const submit = () => {
    setSubmitted(true);
    if (!isValid || creating || updating || savingRoadmap) return;

    if (isEdit && student) {
      const studentId = `StudentNode:${splitId(student.id)}`;
      if (isExternal) {
        saveRoadmapIfChanged(studentId, finishEdit);
        return;
      }
      updateStudentMutation({
        variables: {
          input: {
            email: values.emailAddress,
            firstName: values.firstName,
            lastName: values.lastName,
            userId: student.id,
            gradeLevelId: values.grade?.id ?? '',
            siteIds: [siteId],
            isActive: true,
            ...(password ? { password } : {}),
          },
        },
        onCompleted: () => saveRoadmapIfChanged(studentId, finishEdit),
        onError: () => showError(),
      });
      return;
    }

    createStudentMutation({
      variables: {
        input: {
          inputs: [
            {
              email: values.emailAddress,
              firstName: values.firstName,
              lastName: values.lastName,
              gradeLevel: values.grade?.id ?? '',
              siteIds: [siteId],
              connection: 'username-passs',
              roleId: 'RoleNode:2',
            },
          ],
        },
      },
      onCompleted: data => {
        const created = data?.createUsers?.createUserResult?.createdUsers?.[0];
        if (!created) {
          showError(data?.createUsers?.errors?.[0]?.message);
          return;
        }
        const studentId = `StudentNode:${splitId(created.id)}`;
        upsertRoadmap({
          variables: {
            input: {
              studentTargetGroups: [
                { studentId, targetGroupId: values.roadmap?.id ?? '', isCurrent: true, isActive: true },
              ],
            },
          },
          onCompleted: () => {
            toast.current?.show({
              severity: 'success',
              detail: (
                <>
                  <MaskedStudentData student={{ publicUserId: created.publicUserId }} type={PiiTypes.FullName}>
                    {`${created.firstName} ${created.lastName}`}
                  </MaskedStudentData>
                  {' was created and added.'}
                </>
              ),
            });
            onCreated?.({
              id: created.id,
              firstName: created.firstName ?? '',
              lastName: created.lastName ?? '',
              gradeName: values.grade?.name,
              email: created.email ?? undefined,
            });
            close();
          },
          onError: () => showError(),
        });
      },
      onError: () => showError(),
    });
  };

  const loading = creating || updating || savingRoadmap;

  const header = (
    <div className={`flex gap-md ${isEdit ? 'align-items-center' : 'align-items-start'}`}>
      <span className="nc_wizard-header-icon">
        {isEdit ? <SquarePen size={17} aria-hidden /> : <UserPlus size={17} aria-hidden />}
      </span>
      <div className="flex flex-column">
        <h2 className="text-xl font-semibold line-height-1 m-0 tw-not-italic">
          {isEdit ? 'Edit student' : 'New student'}
        </h2>
        {!isEdit && (
          <span className="text-color-secondary text-sm tw-leading-5 mt-1 tw-font-normal">
            New users will receive an email letting them know their account has been created and how to log in.
          </span>
        )}
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-content-between align-items-center gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={close} disabled={loading} />
      <Button label="Save" icon="nc_icon-nc_save icon-sm" rounded onClick={submit} loading={loading} />
    </div>
  );

  return (
    <Dialog
      header={header}
      footer={footer}
      visible={visible}
      onHide={close}
      className="nc_class-wizard"
      style={{ width: '32.5rem' }}
      dismissableMask={false}
      ariaCloseIconLabel="Close"
    >
      <div className="nc_wizard-form flex flex-column gap-md py-2">
        <div className="grid">
          <div className="col-6 flex flex-column gap-xs">
            <label htmlFor="wizardStudentFirstName" className="font-semibold">
              First name <span className="nc_required-mark">*</span>
            </label>
            <MaskedInput>
              <InputText
                id="wizardStudentFirstName"
                disabled={isExternal}
                value={values.firstName}
                onChange={event => setValues(prev => ({ ...prev, firstName: event.target.value }))}
                className="w-full"
              />
            </MaskedInput>
            {submitted && errors.firstName && <small className="p-error">{errors.firstName}</small>}
          </div>
          <div className="col-6 flex flex-column gap-xs">
            <label htmlFor="wizardStudentLastName" className="font-semibold">
              Last name <span className="nc_required-mark">*</span>
            </label>
            <MaskedInput>
              <InputText
                id="wizardStudentLastName"
                disabled={isExternal}
                value={values.lastName}
                onChange={event => setValues(prev => ({ ...prev, lastName: event.target.value }))}
                className="w-full"
              />
            </MaskedInput>
            {submitted && errors.lastName && <small className="p-error">{errors.lastName}</small>}
          </div>
        </div>

        <div className="flex flex-column gap-xs">
          <label htmlFor="wizardStudentEmail" className="font-semibold">
            Email address <span className="nc_required-mark">*</span>
          </label>
          <MaskedInput>
            <InputText
              id="wizardStudentEmail"
              disabled={isExternal}
              value={values.emailAddress}
              onChange={event => setValues(prev => ({ ...prev, emailAddress: event.target.value }))}
              className="w-full"
            />
          </MaskedInput>
          {submitted && errors.emailAddress && <small className="p-error">{errors.emailAddress}</small>}
        </div>

        <div className="grid">
          <div className="col-6 flex flex-column gap-xs">
            <label htmlFor="wizardStudentGrade" className="font-semibold">
              Grade <span className="nc_required-mark">*</span>
            </label>
            <Dropdown
              inputId="wizardStudentGrade"
              disabled={isExternal}
              value={values.grade}
              options={grades}
              optionLabel="name"
              dataKey="id"
              onChange={event => setValues(prev => ({ ...prev, grade: event.value as Grade }))}
              placeholder="Select grade"
              className="w-full"
            />
            {submitted && errors.grade && <small className="p-error">{errors.grade}</small>}
          </div>
          <div className="col-6 flex flex-column gap-xs">
            <label htmlFor="wizardStudentSite" className="font-semibold">
              Site <span className="nc_required-mark">*</span>
            </label>
            {siteOptions && !isEdit ? (
              <Dropdown
                inputId="wizardStudentSite"
                value={siteOptions.find(site => site.id === siteId) ?? null}
                options={siteOptions}
                optionLabel="name"
                dataKey="id"
                onChange={event => {
                  onSiteChange?.(event.value);
                  setValues(prev => ({ ...prev, roadmap: null }));
                }}
                placeholder="Select site"
                className="w-full"
              />
            ) : (
              <InputText id="wizardStudentSite" value={siteName} disabled className="w-full" />
            )}
            {submitted && errors.site && <small className="p-error">{errors.site}</small>}
          </div>
        </div>

        <div className="flex flex-column gap-xs">
          <label htmlFor="wizardStudentRoadmap" className="font-semibold">
            Roadmap <span className="nc_required-mark">*</span>
          </label>
          <Dropdown
            inputId="wizardStudentRoadmap"
            value={values.roadmap}
            options={roadmapOptions}
            optionLabel="name"
            dataKey="id"
            onChange={event => setValues(prev => ({ ...prev, roadmap: event.value as TargetGroup }))}
            placeholder="Select roadmap"
            className="w-full"
          />
          {isEdit && isUserInMoreThanOneClass && roadmapChanged && (
            <div className="flex align-items-center justify-content-between gap-md p-2 border-round-md my-1 tw-bg-amber-50">
              <span className="flex align-items-center gap-xs font-semibold tw-text-amber-700">
                <i className="nc_icon-nc_warning icon-sm" />
                This action will change this student&apos;s Roadmap in all their classes.
              </span>
              <Button
                onClick={() => setValues(prev => ({ ...prev, roadmap: currentRoadmap ?? null }))}
                className="h-2rem flex-shrink-0"
                label="Restore"
                outlined
              />
            </div>
          )}
          {submitted && errors.roadmap && <small className="p-error">{errors.roadmap}</small>}
        </div>

        {isEdit && !isExternal && (
          <div className="flex flex-column gap-xs">
            <label htmlFor="wizardStudentPassword" className="font-semibold flex align-items-center gap-xs">
              Set new password
              <span className="nc_wizard-form__info-icon" id="wizardStudentPasswordTooltip">
                <Info size={16} aria-hidden />
              </span>
            </label>
            <Tooltip target="#wizardStudentPasswordTooltip">Existing passwords cannot be shown for security.</Tooltip>
            <Password
              inputId="wizardStudentPassword"
              value={password}
              onChange={event => setPassword(event.target.value)}
              toggleMask
              feedback={false}
              className="w-full"
              inputClassName="w-full"
            />
            {password && (
              <div className="flex flex-column gap-xs text-sm">
                <span className="font-semibold text-color-secondary tw-uppercase tw-text-xs">
                  Password must include
                </span>
                <ul className="m-0 p-0 flex flex-column gap-xs" style={{ listStyle: 'none' }}>
                  <ChecklistItem met={passwordChecks.minLength}>At least 10 characters in length</ChecklistItem>
                  <ChecklistItem met={passwordMeetsThreeClasses}>
                    At least three of the following ({passwordClassesMet}/3):
                  </ChecklistItem>
                  <ul className="pl-4 m-0 flex flex-column gap-xs" style={{ listStyle: 'none' }}>
                    <ChecklistSubItem met={passwordChecks.hasLower}>Lower case letters (a-z)</ChecklistSubItem>
                    <ChecklistSubItem met={passwordChecks.hasUpper}>Upper case letters (A-Z)</ChecklistSubItem>
                    <ChecklistSubItem met={passwordChecks.hasNumber}>Numbers (0-9)</ChecklistSubItem>
                    <ChecklistSubItem met={passwordChecks.hasSpecial}>Special characters (ex. !@#)</ChecklistSubItem>
                  </ul>
                  <ChecklistItem met={passwordChecks.noTripleRepeat}>
                    No more than 2 identical characters in a row
                  </ChecklistItem>
                </ul>
              </div>
            )}
            {submitted && errors.password && <small className="p-error">{errors.password}</small>}
          </div>
        )}

        <span className="text-color-secondary text-sm">
          <span className="nc_required-mark">*</span> Required fields
        </span>
      </div>
    </Dialog>
  );
};

export default ClassWizardNewStudentDialog;
